import { AlertTriangle, Plus, RotateCcw } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { defaultKeybindings } from '../model/defaultKeybindings';
import type { KeybindingCommandId, KeyboardLayoutSnapshot, Platform } from '../model/keybindingTypes';
import { isSchemeRow } from '../model/schemes/schemeRows';
import { ChordRecorder } from './ChordRecorder';
import { formattedChord } from '../hooks/useShortcutLabel';
import { availableKeybindingSchemes, schemeFor } from '../store/keybindingSchemes';
import { useKeybindingsStore } from '../store/keybindingsStore';
import { MAX_CHORDS_PER_COMMAND } from '../store/normalizeOverrides';
import type { KeybindingEditActions, RecordingMode } from './useKeybindingEditActions';

export type KeybindingRowWarning = { readonly key: string; readonly text: string };

export const CHORD_LIMIT_MESSAGE = `A command can have at most ${MAX_CHORDS_PER_COMMAND} shortcuts.`;

export type KeybindingRowProps = {
  readonly commandId: KeybindingCommandId;
  /** The command's stored chords (override or scheme), in order. */
  readonly chords: readonly string[];
  /** The subset validation left active; the rest are shown struck through. */
  readonly activeChords: readonly string[];
  readonly customised: boolean;
  readonly warnings: readonly KeybindingRowWarning[];
  readonly recording: RecordingMode | null;
  readonly onRecord: (mode: RecordingMode | null) => void;
  readonly actions: KeybindingEditActions;
  readonly platform: Platform;
  readonly layout: KeyboardLayoutSnapshot;
};

const chipClass =
  'inline-flex h-7 items-center rounded-md border border-border bg-muted px-2 font-mono text-xs text-foreground hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * Where the row's shortcuts come from: the user's own override, a row of the
 * selected scheme that differs from Jam SQL, or the Jam SQL default.
 */
type BadgeKind = 'customised' | 'scheme' | 'default';
const BADGE_TEXT: Readonly<Record<BadgeKind, string>> = {
  customised: 'Customised',
  scheme: 'Scheme default',
  default: 'Default',
};

/** `keybinding-record-<id>` is the first chip (or the empty-state button); later chips add `-<index>`. */
const recordTestId = (commandId: KeybindingCommandId, index: number): string =>
  index === 0 ? `keybinding-record-${commandId}` : `keybinding-record-${commandId}-${index}`;

export function KeybindingRow({
  commandId,
  chords,
  activeChords,
  customised,
  warnings,
  recording,
  onRecord,
  actions,
  platform,
  layout,
}: KeybindingRowProps) {
  const definition = defaultKeybindings[commandId];
  const scheme = schemeFor(useKeybindingsStore((state) => state.scheme), availableKeybindingSchemes);
  const badge: BadgeKind = customised ? 'customised' : isSchemeRow(scheme, commandId, platform) ? 'scheme' : 'default';
  const atChordLimit = chords.length >= MAX_CHORDS_PER_COMMAND;
  const active = { commandId, mode: recording ?? { kind: 'add' as const } };
  const recorder = (
    <ChordRecorder
      commandId={commandId}
      platform={platform}
      onChord={(chord) => {
        actions.commit(active, chords, chord);
        onRecord(null);
      }}
      onClear={() => {
        actions.clear(active);
        onRecord(null);
      }}
      onCancel={() => {
        actions.cancel(commandId);
        onRecord(null);
      }}
      onReject={() => actions.reject(commandId)}
    />
  );

  return (
    <div
      className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_7rem_2rem] items-start gap-3 px-3 py-2"
      data-testid={`keybinding-row-${commandId}`}
    >
      <div className="pt-1 text-sm">{definition.label}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {chords.map((chord, index) => {
          if (recording?.kind === 'replace' && recording.chord === chord) {
            return <span key={chord}>{recorder}</span>;
          }
          const inactive = !activeChords.includes(chord);
          return (
            <button
              key={chord}
              type="button"
              className={cn(chipClass, inactive && 'text-muted-foreground line-through')}
              title={inactive ? 'Inactive — see the note below. Click to change.' : 'Click to change'}
              data-testid={recordTestId(commandId, index)}
              onClick={() => onRecord({ kind: 'replace', chord })}
            >
              {formattedChord(chord, platform, layout)}
            </button>
          );
        })}
        {recording?.kind === 'add' && recorder}
        {recording?.kind !== 'add' && chords.length === 0 && (
          <button
            type="button"
            className={cn(chipClass, 'border-dashed bg-transparent font-sans text-muted-foreground')}
            data-testid={recordTestId(commandId, 0)}
            onClick={() => onRecord({ kind: 'add' })}
          >
            Not bound
          </button>
        )}
        {recording?.kind !== 'add' && chords.length > 0 && (
          <Button
            variant="ghost"
            size="xs"
            className="h-7 w-7 px-0"
            title={atChordLimit ? CHORD_LIMIT_MESSAGE : 'Add another shortcut'}
            aria-label={`Add another shortcut for ${definition.label}`}
            disabled={atChordLimit}
            data-testid={`keybinding-add-${commandId}`}
            onClick={() => onRecord({ kind: 'add' })}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="pt-1">
        <Badge
          variant={badge === 'customised' ? 'secondary' : 'outline'}
          className={cn('whitespace-nowrap font-normal', badge === 'default' && 'border-transparent text-muted-foreground')}
          title={badge === 'scheme' ? `Default of the ${scheme.label} scheme` : undefined}
          data-testid={`keybinding-badge-${commandId}`}
        >
          {BADGE_TEXT[badge]}
        </Badge>
      </div>
      <div>
        {customised && (
          <Button
            variant="ghost"
            size="xs"
            className="h-7 w-7 px-0"
            title="Reset to the scheme default"
            aria-label={`Reset ${definition.label} to the scheme default`}
            data-testid={`keybinding-reset-${commandId}`}
            onClick={() => actions.reset(commandId)}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {atChordLimit && (
        <p className="col-span-full text-xs text-muted-foreground" data-testid={`keybinding-limit-${commandId}`}>
          {CHORD_LIMIT_MESSAGE}
        </p>
      )}
      {warnings.length > 0 && (
        <ul className="col-span-full space-y-1" data-testid={`keybinding-warnings-${commandId}`}>
          {warnings.map((warning) => (
            <li key={warning.key} className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{warning.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
